// What store owners and staff do in the admin panel, reported for the Super Admin's Admin Tracker (Analytics ->
// Admin Tracker; backend services/adminTracker.js): the panel opening and closing, leaving it and coming back, the
// tabs, pages and buttons used, and a heartbeat every 30 seconds while it is on screen (the time spent). Only names of
// tabs and buttons are sent, never what is typed. Super Admins, partners and anyone "viewing as" are not tracked.
import { TOKEN_KEY } from "../services";
import { viewingAsBy } from "./viewAs";

const ENDPOINT = `${process.env.REACT_APP_API_URL || "/api"}/admin/activity`;
const SESSION_KEY = "oz_admin_session";
const TRACKED_ROLES = ["storeadmin", "staff"];
const HEARTBEAT_MS = 30 * 1000;
const FLUSH_MS = 15 * 1000;
const AWAY_MS = 30 * 1000; // leaving the panel for less than this is not worth a line in the timeline

let active = false;
let queue = [];
let visibleSince = 0;
let hiddenAt = 0;
let timers = [];
let lastClick = { label: "", at: 0 };

const sessionId = () => {
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

// Sends what is queued. keepalive lets the last report out while the tab closes.
function flush(closing = false) {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!queue.length || !token) return;
  const events = queue.splice(0, 50);
  fetch(ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json", "x-auth-token": token },
    body: JSON.stringify({ sessionId: sessionId(), events }),
    keepalive: closing,
  }).catch(() => {});
  if (queue.length) flush(closing);
}

export function track(type, label = "", site = "") {
  if (!active) return;
  queue.push({ type, label: String(label || "").slice(0, 120), site: String(site || "").slice(0, 80), at: new Date().toISOString() });
}

// Screen time since the last heartbeat, while the panel is visible
function heartbeat() {
  if (!active || document.visibilityState !== "visible" || !visibleSince) return;
  const now = Date.now();
  queue.push({ type: "heartbeat", ms: now - visibleSince, at: new Date(now).toISOString() });
  visibleSince = now;
}

function onVisibility() {
  if (document.visibilityState === "hidden") {
    heartbeat();
    hiddenAt = Date.now();
    visibleSince = 0;
    flush(true);
  } else {
    const away = hiddenAt ? Date.now() - hiddenAt : 0;
    if (away >= AWAY_MS) {
      queue.push({ type: "panel_hidden", at: new Date(hiddenAt).toISOString() });
      track("panel_visible", `After ${Math.round(away / 60000) || "<1"} min away`);
    }
    hiddenAt = 0;
    visibleSince = Date.now();
  }
}

function onPageHide() {
  heartbeat();
  track("panel_close");
  flush(true);
}

// Buttons, links and tabs clicked: their visible name (or aria-label), not the page's text around them
function onClick(event) {
  const el = event.target && event.target.closest && event.target.closest("button, a, [role=tab], [role=button]");
  if (!el || el.closest("[data-no-track]")) return;
  const label = (el.getAttribute("aria-label") || el.textContent || "").replace(/\s+/g, " ").trim().slice(0, 60);
  if (!label) return;
  const now = Date.now();
  if (label === lastClick.label && now - lastClick.at < 1500) return; // double clicks
  lastClick = { label, at: now };
  const site = (window.location.pathname.match(/^\/admin\/sites\/([^/]+)/) || [])[1] || "";
  track("click", label, site);
}

// Starts tracking for a signed-in store owner or staff member (once per page load)
export function startTracking(user) {
  if (active || !user || !TRACKED_ROLES.includes(user.role) || viewingAsBy()) return;
  active = true;
  const isNew = !sessionStorage.getItem(SESSION_KEY);
  sessionId();
  track("panel_open", isNew ? "Opened" : "Reloaded");
  visibleSince = document.visibilityState === "visible" ? Date.now() : 0;
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("pagehide", onPageHide);
  document.addEventListener("click", onClick, true);
  timers = [setInterval(heartbeat, HEARTBEAT_MS), setInterval(() => flush(), FLUSH_MS)];
  flush();
}

// Signing out: report the end of the session and stop
export function stopTracking() {
  if (!active) return;
  heartbeat();
  track("panel_close", "Signed out");
  flush(true);
  active = false;
  timers.forEach(clearInterval);
  timers = [];
  document.removeEventListener("visibilitychange", onVisibility);
  window.removeEventListener("pagehide", onPageHide);
  document.removeEventListener("click", onClick, true);
  sessionStorage.removeItem(SESSION_KEY);
}
